import React, { useEffect, useState, useCallback, useMemo, useReducer } from "react";
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  Chip,
  LinearProgressProps,
  LinearProgress,
  Checkbox
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DownloadIcon from "@mui/icons-material/Download";
import { ViewerComponent } from "../../components/viewer/ViewerComponent";
import { ViewerProvider } from "../../contexts/ViewerContext/ViewerProvider";
import { ViewCubeComponent } from "../../components/viewer/ViewCubeComponent";
import { InfoBox } from "../../components/InfoBox";
import { useLoadingPlan } from "../../contexts/LoadingPlanContext";
import { DataTable, Column } from "../../components/ui/DataTable";
import "./ViewSharedLoadingPlanPage.scss";
import { loadingPlansService, partsService } from "../../services";
import { useParams } from "react-router-dom";
import { LoadingPlanStatus, LoadingPlanStatusLabels } from "../../types/loadingPlan";

// Loading workflow types
enum LoadingStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  FINALIZED = 'FINALIZED'
}

enum RowStatus {
  NOT_LOADED = 'NOT_LOADED',
  LOADED = 'LOADED',
  LOCKED = 'LOCKED'
}

// Reducer types
interface PartLoadingState {
  status: RowStatus;
  layer: number;
  name?: string;
  mark: string;
  sequenceNumber: number;
  type: string;
  subType: string;
  qty: number;
  weight: number;
}

interface LoadingState {
  parts: {
    [key: string]: PartLoadingState;
  };
  layers: {
    [key: string]: boolean;
  };
}

type LoadingAction =
  | { type: 'INITIALIZE'; payload: { parts: any[]; partsMap: Map<string, any> } }
  | { type: 'SET_PART_STATUS'; payload: { partId: string; status: RowStatus } }
  | { type: 'SET_LAYER_LOADED'; payload: { layer: number; isLoaded: boolean } }
  | { type: 'FINALIZE_ALL' };

// Reducer function
function loadingReducer(state: LoadingState, action: LoadingAction): LoadingState {
  switch (action.type) {
    case 'INITIALIZE': {
      const parts: { [key: string]: PartLoadingState } = {};
      const layers: { [key: string]: boolean } = {};

      action.payload.parts.forEach((part: any) => {
        const partId = part.part_id;
        const layer = part.assigned_loading_layer || 1;
        const basePartId = partId.includes('#') ? partId.split('#')[0] : partId;
        const isPallet = basePartId === 'pallet';

        let name: string | undefined;
        let mark: string;
        let type: string;
        let subType: string;
        let weight: number;
        let sequenceNumber: number = 0;

        if (isPallet) {
          const childItems = part.child_items || [];
          const childWeight = childItems.reduce((sum: number, child: any) => {
            const childIdParts = child.part_id.split('_');
            childIdParts.shift();
            const childGlobalId = childIdParts.join('_');
            const childInfo = action.payload.partsMap.get(childGlobalId);
            return sum + (childInfo?.weight || 0);
          }, 0);
          name = `Pallet (${childItems.length} item${childItems.length !== 1 ? 's' : ''})`;
          mark = partId;
          type = 'Pallet';
          subType = '-';
          weight = parseFloat(childWeight.toFixed(2));
        } else {
          const idParts = partId.split('_');
          idParts.shift();
          const globalId = idParts.join('_');
          const partInfo = action.payload.partsMap.get(globalId);
          name = partInfo?.name;
          mark = partInfo?.mark || partId;
          type = partInfo?.type || '-';
          subType = partInfo?.subType || '-';
          weight = parseFloat((partInfo?.weight || 0).toFixed(2));
          sequenceNumber = partInfo?.sequenceNumber || 0;
        }

        parts[partId] = {
          status: RowStatus.NOT_LOADED,
          layer,
          name,
          mark,
          type,
          subType,
          qty: 1,
          weight,
          sequenceNumber,
        };
      });

      const layerSet = new Set(action.payload.parts.map((p: any) => p.assigned_loading_layer || 1));
      layerSet.forEach((layer) => {
        layers[`isLayerLoaded${layer}`] = false;
      });

      return { parts, layers };
    }

    case 'SET_PART_STATUS': {
      const { partId, status } = action.payload;
      const partState = state.parts[partId];

      if (!partState) return state;

      const newParts = {
        ...state.parts,
        [partId]: {
          ...partState,
          status,
        },
      };

      const layer = partState.layer;
      const partsInLayer = Object.values(newParts).filter(part => part.layer === layer);
      const allLoaded = partsInLayer.every((part) => part.status === RowStatus.LOADED);

      const newLayers = {
        ...state.layers,
        [`isLayerLoaded${layer}`]: allLoaded,
      };

      return { parts: newParts, layers: newLayers };
    }

    case 'SET_LAYER_LOADED': {
      return {
        ...state,
        layers: {
          ...state.layers,
          [`isLayerLoaded${action.payload.layer}`]: action.payload.isLoaded,
        },
      };
    }

    case 'FINALIZE_ALL': {
      const newParts = { ...state.parts };
      Object.keys(newParts).forEach((key) => {
        newParts[key] = {
          ...newParts[key],
          status: RowStatus.LOCKED,
        };
      });
      return { ...state, parts: newParts };
    }

    default:
      return state;
  }
}

interface PartRow {
  id: number;
  partId: string; // unique identifier for the part
  mark: string;
  type: string;
  subType: string;
  qty: number;
  weight: number;
  layer: string;
  layerNumber: number;
  rowStatus: RowStatus;
  status?: React.ReactNode;
}

function LinearProgressWithLabel(props: LinearProgressProps & { value: number }) {
  return (
    <Box sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
      <Box sx={{ width: '100%' }}>
        <LinearProgress variant="determinate" {...props} />
      </Box>
      <Box sx={{ minWidth: 90, ml: 2 }}>
        <Typography
          variant="body2"
          sx={{ color: 'text.secondary' }}
        >{`${Math.round(props.value)}% loaded`}</Typography>
      </Box>
    </Box>
  );
}

const ViewSharedLoadingPlanPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>(LoadingStatus.NOT_STARTED);
  const [progressPercent, setProgressPercent] = useState(0);
  const [currentPartId, setCurrentPartId] = useState<string | null>(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const [viewerNavigationCommand, setViewerNavigationCommand] = useState<'next' | 'prev' | null>(null);

  const [loadingState, dispatch] = useReducer(loadingReducer, { parts: {}, layers: {} });

  const { currentLoadingPlan, setCurrentLoadingPlan, updateCurrentLoadingPlan } = useLoadingPlan();

  const { loadingPlanId } = useParams();

  const baseColumns: Column<PartRow>[] = [
    { id: 'id', label: '#', className: 'table-cell-number', width: 60 },
    { id: 'name', label: 'Name', className: 'table-cell-name', minWidth: 100 },
    { id: 'mark', label: 'Mark', className: 'table-cell-mark', minWidth: 100 },
    { id: 'sequenceNumber', label: 'Sequence', className: 'table-cell-sequence', minWidth: 100 },
    { id: 'type', label: 'Type', className: 'table-cell-type', minWidth: 120 },
    { id: 'subType', label: 'Sub Type', className: 'table-cell-sub-type', minWidth: 120 },
    { id: 'qty', label: 'Qty', className: 'table-cell-qty', minWidth: 80, align: 'right' },
    { id: 'weight', label: 'Weight, lbs', className: 'table-cell-weight', minWidth: 120, align: 'right' },
    { id: 'layer', label: 'Layer', className: 'table-cell-layer', minWidth: 100 },
  ];

  const statusColumn: Column<PartRow> = {
    id: 'status',
    label: 'Status',
    className: 'table-cell-status',
    minWidth: 120,
  };

  const isLoadingActive = loadingStatus === LoadingStatus.IN_PROGRESS ||
    loadingStatus === LoadingStatus.COMPLETED ||
    loadingStatus === LoadingStatus.FINALIZED;
  const columns = isLoadingActive ? [...baseColumns, statusColumn] : baseColumns;

  const lastPartId = useMemo(() => {
    const partIds = Object.keys(loadingState.parts);
    return partIds.length > 0 ? partIds[partIds.length - 1] : null;
  }, [loadingState.parts]);

  const handleCheckboxToggle = useCallback((partId: string, checked: boolean) => {
    const isCompleted = loadingStatus === LoadingStatus.COMPLETED;
    const isLastPart = partId === lastPartId;

    if (loadingStatus !== LoadingStatus.IN_PROGRESS && !(isCompleted && isLastPart)) return;
    if (!isCompleted && partId !== currentPartId) return;

    dispatch({
      type: 'SET_PART_STATUS',
      payload: { partId, status: checked ? RowStatus.LOADED : RowStatus.NOT_LOADED }
    });

    if (isCompleted && isLastPart && !checked) {
      setLoadingStatus(LoadingStatus.IN_PROGRESS);
      setCurrentPartId(partId);
    }
  }, [loadingStatus, currentPartId, lastPartId]);

  const allPlanRows: PartRow[] = useMemo(() => {
    const partIds = Object.keys(loadingState.parts);

    if (partIds.length === 0) return [];

    return partIds.map((partId, index) => {
      const partState = loadingState.parts[partId];
      const currentRowStatus = partState.status;

      return {
        id: index + 1,
        partId,
        name: partState.name,
        mark: partState.mark,
        type: partState.type,
        sequenceNumber: partState.sequenceNumber,
        subType: partState.subType,
        qty: partState.qty,
        weight: partState.weight,
        layer: `Layer ${partState.layer}`,
        layerNumber: partState.layer,
        rowStatus: currentRowStatus,
        status: isLoadingActive ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Checkbox
              size="small"
              checked={currentRowStatus === RowStatus.LOADED || currentRowStatus === RowStatus.LOCKED}
              disabled={loadingStatus === LoadingStatus.FINALIZED || (loadingStatus !== LoadingStatus.COMPLETED && currentPartId !== partId) || (loadingStatus === LoadingStatus.COMPLETED && partId !== lastPartId)}
              onChange={(e) => handleCheckboxToggle(partId, e.target.checked)}
            />
            <Typography variant="body2">Loaded</Typography>
          </Box>
        ) : undefined
      };
    });
  }, [loadingState, isLoadingActive, loadingStatus, currentPartId, lastPartId, handleCheckboxToggle]);

  const planRows = activeTab === 0
    ? allPlanRows
    : allPlanRows.filter(row => row.layer === `Layer ${activeTab}`);

  const isCurrentPartLoaded = useMemo(() => {
    if (!currentPartId) return false;
    const partState = loadingState.parts[currentPartId];
    return partState?.status === RowStatus.LOADED;
  }, [currentPartId, loadingState]);

  const handleTabChange = (_: any, newValue: number) => {
    setActiveTab(newValue);
  };

  useEffect(() => {
    const partIds = Object.keys(loadingState.parts);
    if (partIds.length === 0) return;

    const loadedCount = partIds.filter(partId => {
      const partState = loadingState.parts[partId];
      return partState.status === RowStatus.LOADED;
    }).length;

    const progress = Math.floor((loadedCount / partIds.length) * 100);

    setProgressPercent(progress);

    if (loadingStatus === LoadingStatus.IN_PROGRESS && progress === 100) {
      setLoadingStatus(LoadingStatus.COMPLETED);
      setCurrentPartId(null);
    }
  }, [loadingState, loadingStatus]);

  const handleStartLoading = () => {
    if (loadingStatus !== LoadingStatus.NOT_STARTED) return;

    setLoadingStatus(LoadingStatus.IN_PROGRESS);
    setActiveTab(1);
    setProgressPercent(0);

    if (allPlanRows.length > 0) {
      setCurrentPartId(allPlanRows[0].partId);
    }
  };

  const handleOverview = () => {
    setActiveTab(0);
  };

  const handlePrev = () => {
    if (loadingStatus === LoadingStatus.FINALIZED) return;

    setViewerNavigationCommand('prev');

    if (loadingStatus === LoadingStatus.COMPLETED) {
      const currentIndex = allPlanRows.findIndex(row => row.partId === currentPartId);
      let prevIndex = currentIndex !== -1 ? currentIndex - 1 : allPlanRows.length - 1;

      if (prevIndex >= 0) {
        const prevPart = allPlanRows[prevIndex];

        if (currentPartId) {
          dispatch({
            type: 'SET_PART_STATUS',
            payload: { partId: currentPartId, status: RowStatus.NOT_LOADED }
          });

          setLoadingStatus(LoadingStatus.IN_PROGRESS);
        }

        setCurrentPartId(prevPart.partId);
        setActiveTab(prevPart.layerNumber);
      }
      return;
    }

    const currentIndex = allPlanRows.findIndex(row => row.partId === currentPartId);
    let prevIndex = currentIndex - 1;

    while (prevIndex >= 0) {
      const partState = loadingState.parts[allPlanRows[prevIndex].partId];
      if (partState?.status === RowStatus.LOADED) break;
      prevIndex--;
    }

    if (prevIndex >= 0) {
      const prevPart = allPlanRows[prevIndex];

      if (currentPartId) {
        dispatch({
          type: 'SET_PART_STATUS',
          payload: { partId: currentPartId, status: RowStatus.NOT_LOADED }
        });
      }

      setCurrentPartId(prevPart.partId);
      setActiveTab(prevPart.layerNumber);
    }
  };

  const handleNext = () => {
    if (loadingStatus === LoadingStatus.FINALIZED) return;

    setViewerNavigationCommand('next');

    const currentIndex = allPlanRows.findIndex(row => row.partId === currentPartId);
    let nextIndex = currentIndex + 1;

    while (nextIndex < allPlanRows.length) {
      const partState = loadingState.parts[allPlanRows[nextIndex].partId];
      if (partState?.status !== RowStatus.LOADED) break;
      nextIndex++;
    }

    if (nextIndex < allPlanRows.length) {
      const nextPart = allPlanRows[nextIndex];
      setCurrentPartId(nextPart.partId);
      setActiveTab(nextPart.layerNumber);
    }
  };

  const handleFinish = async () => {
    if (loadingStatus !== LoadingStatus.COMPLETED) return;

    setIsCompleted(true);
    setLoadingStatus(LoadingStatus.FINALIZED);

    dispatch({ type: 'FINALIZE_ALL' });

    await loadingPlansService.setLoadedStatus(currentLoadingPlan.shareToken as string);
  };

  const handleViewLoadingPlan = async () => {
    window.location.reload();
  };

  const handleExportPdf = async () => {
    if (!currentLoadingPlan.shareToken) return;
    try {
      await loadingPlansService.exportSharedPdf(currentLoadingPlan.shareToken as string);
    } catch (error) {
      console.error('Failed to export PDF:', error);
    }
  };

  const layersCount = currentLoadingPlan.nestingOutput?.nested_parts
    ? Math.max(...currentLoadingPlan.nestingOutput.nested_parts.map((part: any) => part.assigned_loading_layer || 1))
    : 0;

  useEffect(() => {
    const fetchAndInitialize = async () => {
      let loadingPlan = currentLoadingPlan;
      if (!currentLoadingPlan.id && loadingPlanId) {
        loadingPlan = await loadingPlansService.fetchSharedLoadingPlanByToken(loadingPlanId);
        setCurrentLoadingPlan(loadingPlan);
        const includedParts = await partsService.fetchLoadingPlanViewParts(loadingPlan.id);
        updateCurrentLoadingPlan({ includedParts: includedParts.parts });
      }

      if (loadingPlan.nestingOutput?.nested_parts && loadingPlan.includedParts) {
        const filteredParts = loadingPlan.nestingOutput.nested_parts.filter((part: any) => {
          const basePartId = part.part_id.includes('#') ? part.part_id.split('#')[0] : part.part_id;
          return basePartId !== 'dunnage';
        });

        const partsMapForInit = new Map(
          (loadingPlan.includedParts || []).map((part: any) => [part.globalId, part])
        );

        dispatch({
          type: 'INITIALIZE',
          payload: {
            parts: filteredParts,
            partsMap: partsMapForInit
          }
        });
      }
    };

    fetchAndInitialize();
  }, [loadingPlanId, currentLoadingPlan.id, setCurrentLoadingPlan, updateCurrentLoadingPlan, currentLoadingPlan]);

  return (
    <ViewerProvider>
      <div className="view-loading-plan-page">
        {
          isCompleted ?
            <Paper className="form-card completed-message">
              <div className="icon">🎉</div>
              <div className="complete-text">Load complete</div>
              <div>All layers marked as loaded. Please strap and finalize paperwork.</div>
              <Chip label={`Published plan: ${currentLoadingPlan.title}`} color="default" />
              <Button variant="contained" color="primary" onClick={handleViewLoadingPlan}>View task</Button>
              <Button variant="outlined" color="primary" startIcon={<DownloadIcon />} onClick={handleExportPdf}>Export PDF</Button>
            </Paper>
            :
            <>
              <Paper className="form-card">
                <Box className="table-container">
                  {/* Page Title and Status */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {currentLoadingPlan.title}
                    </Typography>
                    <Chip
                      label={LoadingPlanStatusLabels[currentLoadingPlan.status as LoadingPlanStatus]}
                      color={
                        currentLoadingPlan.status === LoadingPlanStatus.PUBLISHED
                          ? "success"
                          : currentLoadingPlan.status === LoadingPlanStatus.READY
                            ? "info"
                            : currentLoadingPlan.status === LoadingPlanStatus.FAILED
                              ? "error"
                              : "default"
                      }
                      variant="outlined"
                      sx={
                        currentLoadingPlan.status === LoadingPlanStatus.DRAFT
                          ? {
                            backgroundColor: "rgba(0, 0, 0, 0.08)",
                            borderColor: "rgba(0, 0, 0, 0.08)",
                          }
                          : {}
                      }
                    />
                  </Box>

                  {/* Stats */}
                  <Box className="stats-row">
                    <InfoBox label="Parts" value={allPlanRows.length || 0} />
                    <InfoBox label="Trailer" value={(currentLoadingPlan?.trailerLength || 0) + " ft"} />
                    <InfoBox label="Max Weight Capacity" value={(currentLoadingPlan?.trailerMaxWeight || 0) + " lbs"} />
                    <InfoBox label="Total Weight" value={(currentLoadingPlan?.includedParts?.reduce((acc, part) => acc + (part.weight || 0), 0) || 0).toFixed(2) + " lbs"} />
                  </Box>
                </Box>

                {/* Viewer Preview */}
                <Box mb={2}>
                  <Box className="viewer-container">
                    <ViewerComponent
                      showNavigation={currentLoadingPlan.status === LoadingPlanStatus.LOADED}
                      navigationCommand={viewerNavigationCommand}
                      onNavigationComplete={() => setViewerNavigationCommand(null)}
                      isStartFromFirstPart={true}
                    />
                    <Box className="view-cube-wrapper">
                      <ViewCubeComponent />
                    </Box>
                  </Box>
                </Box>
              </Paper>
              {
                currentLoadingPlan.status === LoadingPlanStatus.PUBLISHED && (
                  <Paper className="form-card">
                    {/* Loading Control */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', borderRadius: "4px", gap: 1, mb: 2, p: 2, pt: 2, border: '1px solid #e0e0e0' }}>
                      {loadingStatus === LoadingStatus.NOT_STARTED ? (
                        <Button variant="contained" color="primary" onClick={handleStartLoading}>
                          START LOADING
                        </Button>
                      ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', px: 2 }}>
                          <Button
                            variant="outlined"
                            color="primary"
                            onClick={handleOverview}
                            startIcon={<ArrowBackIcon color="primary" />}
                            disabled={loadingStatus === LoadingStatus.FINALIZED}
                          >
                            OVERVIEW
                          </Button>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              variant="outlined"
                              color="primary"
                              onClick={handlePrev}
                              startIcon={<ArrowBackIcon />}
                              disabled={!Object.values(loadingState.parts).some(part => part.status === RowStatus.LOADED)}
                            >
                              PREV
                            </Button>
                            {loadingStatus === LoadingStatus.COMPLETED ? (
                              <Button
                                variant="contained"
                                color="primary"
                                onClick={handleFinish}
                              >
                                FINISH
                              </Button>
                            ) : (
                              <Button
                                variant="contained"
                                color="primary"
                                onClick={handleNext}
                                endIcon={<ArrowForwardIcon />}
                                disabled={loadingStatus === LoadingStatus.FINALIZED || !isCurrentPartLoaded}
                              >
                                NEXT
                              </Button>
                            )}
                          </Box>
                        </Box>
                      )}
                      <LinearProgressWithLabel value={progressPercent} />
                    </Box>
                  </Paper>
                )
              }
              <Paper className="form-card">
                <Tabs value={activeTab} onChange={handleTabChange} className="tabs-container">
                  <Tab label="ALL" />
                  {Array.from({ length: layersCount }, (_, i) => (
                    <Tab key={i + 1} label={`LAYER ${i + 1}`} />
                  ))}
                </Tabs>

                {/* Table */}
                <Box className="table-container" sx={{ marginBottom: "0px !important" }}>
                  <DataTable
                    columns={columns}
                    data={planRows}
                    size="small"
                    maxHeight={400}
                    emptyMessage="No parts included in this loading plan."
                    className="table-content"
                    headerClassName="table-head"
                  />
                </Box>
              </Paper>
              <Paper className="form-card export-pdf-container">
                <Button variant="outlined" color="primary" onClick={handleExportPdf}>
                  EXPORT PDF
                </Button>
              </Paper>
            </>
        }
      </div>
    </ViewerProvider>
  );
};

export { ViewSharedLoadingPlanPage };
